import { useMemo } from 'react';

import { useTrack } from '@blockworks/platform/services';

import { shareMutations } from '@/api/share/share.mutations';
import { useSyncToken } from '@/context/sync';
import EditorialConfig from '@/editorial-config';

const openShareWindow = (link: string) => {
    const shareMessage = `Check out this visualization from @Blockworks_: ${link}`;
    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}`;
    window.open(tweetUrl, '_blank', 'scrollbars=yes,resizable=yes,toolbar=no,location=yes,width=550,height=420');
};

const useSocialSettings = (dashboardId: string, dashboardSlug: string) => {
    const uploadImage = shareMutations.uploadImage.use({
        onSuccess: (data, variables) => {
            track?.(`Visualization > Shareable Screenshot Created`, {
                screenshotUrl: data?.shareableUrl,
                fileName: [data?.name],
                ...variables.metadata,
            });
        },
    });

    const track = useTrack();
    const syncToken = useSyncToken();
    const settings = useMemo(
        () => ({
            message: 'Share on X',
            callback: async (
                { action, visualization, blob }: { action: string; visualization: any; blob?: Blob },
                ctx: { setMessage: (message: string) => void },
            ) => {
                let uploadImageRes;
                if (blob) {
                    try {
                        uploadImageRes = await uploadImage.mutateAsync({
                            token: syncToken,
                            image: blob,
                            metadata: {
                                title: visualization.title,
                                visualization_id: visualization.id,
                                dashboard_id: dashboardId,
                            },
                        });
                    } catch (err) {
                        ctx.setMessage('❌ Something went wrong, please try again');
                        console.error('Error uploading image', err);
                        return;
                    }
                }

                if (action === 'share-link') {
                    const visualizationUrl = `${EditorialConfig.siteUrl}/analytics/${dashboardSlug}/${visualization.slug}/`;
                    openShareWindow(visualizationUrl);
                } else if (action === 'copy-link') {
                    try {
                        const visualizationUrl = `${EditorialConfig.siteUrl}/analytics/${dashboardSlug}/${visualization.slug}/`;
                        await navigator.clipboard.writeText(visualizationUrl);
                        ctx.setMessage('✔️ Copied to clipboard');
                    } catch (err) {
                        ctx.setMessage('❌ Something went wrong, please try again');
                        console.error('Error sharing visualization', err);
                    }
                } else if (action === 'copy-screenshot-link') {
                    try {
                        if (uploadImageRes?.shareableUrl) {
                            await navigator.clipboard.writeText(uploadImageRes?.shareableUrl);
                            ctx.setMessage('✔️ Copied to clipboard');
                        }
                    } catch (err) {
                        ctx.setMessage('❌ Something went wrong, please try again');
                        console.error('Error sharing visualization', err);
                    }
                } else if (action === 'share-screenshot') {
                    try {
                        if (uploadImageRes?.shareableUrl) {
                            openShareWindow(uploadImageRes!.shareableUrl);
                        }
                    } catch (err) {
                        ctx.setMessage('❌ Something went wrong, please try again');
                        console.error('Error sharing visualization', err);
                    }
                }
            },
        }),
        [dashboardId, dashboardSlug, syncToken, uploadImage],
    );
    return settings;
};

export { useSocialSettings };
